// DCF display rules for the fields the pipeline publishes since 2026-09-12
// (StockApp docs/dcf-fix-plan.md D1 + the growth flag) and 2026-09-15
// (locked decision 18: rate floors + input-integrity filters):
//   model_range  in_range | above_range | below_range | flagged  (missing = in_range)
//   filters      plausibility rules that fired ([] when in range); since decision 18
//                a row with ANY filter is out of range ('flagged' when the discount
//                itself is in band)
//   growth_flag  clamped_high | clamped_low | assumed | null
// Out-of-range rows are published (previously culled) so a ticker is never
// quietly absent; the table flags them, caps the displayed discount at
// +/-200 % and sorts them last by default. Pure helpers, tested without React.

export const DISPLAY_CAP_PCT = 200;
export const BEYOND_RANGE_TEXT = 'beyond model range';

export const RANGE_LABEL = {
  in_range: null,
  above_range: 'price far above DCF',
  below_range: 'DCF far above price',
};

// Decision-18 filters (and the step-2 ones that can fire with an in-band discount).
export const FILTER_LABEL = {
  discount_below_floor: 'price far above DCF',
  discount_above_ceiling: 'DCF far above price',
  price_ratio_extreme: 'price ratio extreme',
  terminal_dominance: 'terminal value dominates',
  levered_stub: 'levered equity stub (debt over half of capital)',
  fcf_gap: 'FCF quarters not contiguous',
  fcf_history_thin: 'under 8 quarters of FCF history',
  fcf_ttm_negative: 'a trailing-12-month FCF was negative',
  fcf_spike: 'trailing FCF over 2x its 3-year average',
};

export const GROWTH_FLAG = {
  clamped_high: { mark: '▲', title: 'Growth pinned at the model ceiling (+12%): the valuation is as high as the model allows, not a measured estimate.' },
  clamped_low: { mark: '▼', title: 'Growth pinned at the model floor (-5%): the valuation is as low as the model allows, not a measured estimate.' },
  assumed: { mark: '≈', title: 'No usable revenue-growth history: the model assumes its default growth.' },
};

const num = (v) => {
  const n = parseFloat(v);
  return Number.isNaN(n) ? null : n;
};

const filtersOf = (row) => (row && Array.isArray(row.filters) ? row.filters : []);

/** true unless the pipeline flagged the row: an out-of-band discount or any filter. */
export function isInRange(row) {
  const range = row && row.model_range;
  const bandOk = !range || range === 'in_range';
  return bandOk && filtersOf(row).length === 0;
}

/** Why a row is out of range, in words: the band label, else the filters. */
export function rangeLabel(row) {
  if (isInRange(row)) return null;
  const band = row.model_range && RANGE_LABEL[row.model_range];
  if (band) return band;
  const named = filtersOf(row).map(f => FILTER_LABEL[f]).filter(Boolean);
  return named.length ? named.join('; ') : BEYOND_RANGE_TEXT;
}

/** Discount cell: capped text past +/-200 % and the range caveat. */
export function discountDisplay(row) {
  const discount = num(row && row.discount_to_dcf);
  if (discount === null) return { text: '—', beyond: false, rangeLabel: null };
  const beyond = Math.abs(discount) > DISPLAY_CAP_PCT;
  const text = beyond
    ? `${discount > 0 ? '>' : '<'} ${discount > 0 ? '' : '-'}${DISPLAY_CAP_PCT}%`
    : `${discount.toFixed(1)}%`;
  return { text, beyond, rangeLabel: rangeLabel(row) };
}

/** FCF GROWTH cell: the number plus an optional provenance mark. */
export function growthDisplay(row) {
  const growth = num(row && row.fcf_growth_rate);
  const flag = row && row.growth_flag ? GROWTH_FLAG[row.growth_flag] : null;
  return {
    text: growth === null ? '—' : `${growth.toFixed(1)}%`,
    positive: growth !== null && growth > 0,
    mark: flag ? flag.mark : null,
    title: flag ? flag.title : null,
  };
}

/** Default order: in-range rows by discount (most undervalued first), then the
 *  flagged rows in the same order. Returns a new array. */
export function sortDcfRows(rows) {
  const list = Array.isArray(rows) ? rows.slice() : [];
  const discount = (r) => num(r && r.discount_to_dcf) ?? 0;
  return list.sort((a, b) => {
    const flagged = Number(!isInRange(a)) - Number(!isInRange(b));
    return flagged !== 0 ? flagged : discount(b) - discount(a);
  });
}

/** Average discount over in-range rows only (null when there are none). */
export function averageInRangeDiscount(rows) {
  const list = (Array.isArray(rows) ? rows : []).filter(isInRange);
  const values = list.map(r => num(r.discount_to_dcf)).filter(v => v !== null);
  if (!values.length) return null;
  return values.reduce((s, v) => s + v, 0) / values.length;
}

/** Counts for the footer / breakdown tiles. */
export function stepTwoCounts(rows) {
  const list = Array.isArray(rows) ? rows : [];
  return {
    outOfRange: list.filter(r => !isInRange(r)).length,
    growthAtBound: list.filter(r => r && (r.growth_flag === 'clamped_high' || r.growth_flag === 'clamped_low')).length,
    growthAssumed: list.filter(r => r && r.growth_flag === 'assumed').length,
  };
}
